import type { ContentCollection, ContentReference } from '@ilteducation/content-collections-types';
import usePatchDiffer from '@/foundations/helpers/uss-pacth-differ';

export type MultiLangVideoTitle = {
  languageCode: string;
  value: string;
};
export type ContentType = { name: string; route: string };

/** Content loaded for a reference, tagged with the reference type it was loaded for. */
export type HydratedContent<T> = { id: string; refType: string; content: T };

/**
 * The surviving references after a collection's realm/context changes.
 *
 * A reference is only dropped when we have its content AND that content fails the check.
 * References whose content has not loaded — still in flight, 404, or a content type with no
 * admin resource — are kept: we cannot prove they don't belong, and dropping them silently
 * deleted curator work on the next save.
 */
export const pruneReferences = <T>(
  references: ContentReference[],
  hydrated: HydratedContent<T>[],
  isAllowed: (content: T) => boolean,
): ContentReference[] => {
  const failing = hydrated.filter((h) => !isAllowed(h.content));
  return references.filter((ref) => !failing.some((h) => h.id === ref.id && h.refType === ref.type));
};

const useCollectionUtils = () => {
  const CollectionsAvailableIcons = [
    'alien',
    'alien8Bit',
    'animals',
    'animals2',
    'ant',
    'appleWhole',
    'atom',
    'backpack',
    'balloon',
    'baseball',
    'baseballBat',
    'basketball',
    'bat',
    'bee',
    'bells',
    'bicycle',
    'binoculars',
    'biohazard',
    'bird',
    'blockQuestion',
    'body',
    'body2',
    'bookBlank',
    'bookOpenLines',
    'bowlingBall',
    'brain',
    'bug',
    'butterfly',
    'cactus',
    'cakeCandles',
    'candy',
    'candyCane',
    'capsule',
    'carBus',
    'carSide',
    'cauldron',
    'chess',
    'christmasTree',
    'circle',
    'circleRadiation',
    'classics',
    'classics2',
    'cloudSun',
    'comet',
    'courtSport',
    'cow',
    'cupcake',
    'dice',
    'difficultQuestions',
    'dinosaur',
    'diploma',
    'dna',
    'dog',
    'dove',
    'downloaded',
    'dragon',
    'duck',
    'dumbbell',
    'empathy',
    'empathy2',
    'family',
    'family',
    'favorite',
    'feather',
    'feelings',
    'fire',
    'flask',
    'football',
    'forest',
    'friendship',
    'frog',
    'galaxy',
    'gamepad',
    'ghost',
    'gift',
    'gingerbreadMan',
    'happy',
    'heart',
    'history',
    'hockey',
    'horseSaddle',
    'jackOLanter',
    'joystick',
    'leaf',
    'lightning',
    'magnet',
    'mathematics',
    'me',
    'medal',
    'microscope',
    'moon',
    'motorcycle',
    'mountain',
    'mugHot',
    'music',
    'nature',
    'news',
    'normsAndValues',
    'otherCountries',
    'pegasus',
    'personChalkboard',
    'personRunning',
    'pinata',
    'pinball',
    'pizzaSlice',
    'planetRinged',
    'popcorn',
    'puzzle',
    'rabbit',
    'racquet',
    'rudolph',
    'satellite',
    'scary',
    'school',
    'seasons',
    'seasons2',
    'shared',
    'shuttleSpaceVertical',
    'snowman',
    'solarSystem',
    'spider',
    'stadium',
    'star',
    'starShooting',
    'sun',
    'sword',
    'tRex',
    'technology',
    'teddyBear',
    'telescope',
    'tennisBall',
    'tentCircus',
    'treasureChest',
    'tree',
    'treePalm',
    'truck',
    'truckMonster',
    'ufo',
    'userAstronaut',
    'vial',
    'volleyball',
    'wave',
    'whale',
    'wind',
  ];

  const icons: { [name: string]: string } = {
    animals: 'fa-regular fa-cat',
    animals2: 'fa-regular fa-dolphin',
    body: 'fa-regular fa-person-waving',
    body2: 'fa-regular fa-person-arms-raised',
    christmasTree: 'fa-regular fa-tree-christmas',
    circle: 'fa-regular fa-circle',
    classics: 'fa-regular fa-unicorn',
    classics2: 'fa-regular fa-hat-wizard',
    difficultQuestions: 'fa-regular fa-user-question',
    downloaded: 'fa-regular fa-circle-down',
    empathy: 'fa-regular fa-handshake-angle',
    empathy2: 'fa-regular fa-hearts',
    favorite: 'fa-regular fa-star',
    feelings: 'fa-regular fa-masks-theater',
    football: 'fa-regular fa-futbol',
    forest: 'fa-regular fa-trees',
    friendship: 'fa-regular fa-user-group',
    happy: 'fa-regular fa-face-smile',
    heart: 'fa-regular fa-heart',
    history: 'fa-regular fa-clock-rotate-left',
    lightning: 'fa-regular fa-bolt-lightning',
    mathematics: 'fa-regular fa-calculator',
    me: 'fa-regular fa-user-circle',
    nature: 'fa-regular fa-seedling',
    news: 'fa-regular fa-newspaper',
    normsAndValues: 'fa-regular fa-users-viewfinder',
    otherCountries: 'fa-regular fa-earth-americas',
    scary: 'fa-regular fa-ghost',
    seasons: 'fa-regular fa-snowflake',
    seasons2: 'fa-regular fa-leaf',
    shared: 'fa-regular fa-users',
    star: 'fa-regular fa-star',
    sun: 'fa-regular fa-sun',
    technology: 'fa-regular fa-robot',
    frog: 'fa-regular fa-frog',
    tRex: 'fa-regular fa-t-rex',
    duck: 'fa-regular fa-duck',
    dog: 'fa-regular fa-dog',
    cow: 'fa-regular fa-cow',
    bat: 'fa-regular fa-bat',
    bee: 'fa-regular fa-bee',
    bird: 'fa-regular fa-bird',
    butterfly: 'fa-regular fa-butterfly',
    pegasus: 'fa-regular fa-pegasus',
    rudolph: 'fa-regular fa-deer-rudolph',
    spider: 'fa-regular fa-spider',
    teddyBear: 'fa-regular fa-teddy-bear',
    thinPhotoFilm: 'fa-thin fa-photo-film',
    whale: 'fa-regular fa-whale',
    binoculars: 'fa-regular fa-binoculars',
    comet: 'fa-regular fa-comet',
    galaxy: 'fa-regular fa-galaxy',
    moon: 'fa-regular fa-moon',
    planetRinged: 'fa-regular fa-planet-ringed',
    satellite: 'fa-regular fa-satellite',
    shuttleSpaceVertical: 'fa-regular fa-shuttle-space-vertical',
    ufo: 'fa-regular fa-ufo',
    alien: 'fa-regular fa-alien',
    userAstronaut: 'fa-regular fa-user-astronaut',
    cactus: 'fa-regular fa-cactus',
    feather: 'fa-regular fa-feather',
    leaf: 'fa-regular fa-leaf',
    mountain: 'fa-regular fa-mountain',
    tree: 'fa-regular fa-tree',
    treePalm: 'fa-regular fa-tree-palm',
    wave: 'fa-regular fa-wave',
    wind: 'fa-regular fa-wind',
    atom: 'fa-regular fa-atom',
    dna: 'fa-regular fa-dna',
    flask: 'fa-regular fa-flask',
    magnet: 'fa-regular fa-magnet',
    microscope: 'fa-regular fa-microscope',
    baseball: 'fa-regular fa-baseball',
    basketball: 'fa-regular fa-basketball',
    bowlingBall: 'fa-regular fa-bowling-ball',
    dumbbell: 'fa-regular fa-dumbbell',
    hockey: 'fa-regular fa-hockey-stick',
    racquet: 'fa-regular fa-racquet',
    stadium: 'fa-regular fa-stadium',
    tennisBall: 'fa-regular fa-tennis-ball',
    volleyball: 'fa-regular fa-volleyball',
    bells: 'fa-regular fa-bells',
    candyCane: 'fa-regular fa-candy-cane',
    gift: 'fa-regular fa-gift',
    gingerbreadMan: 'fa-regular fa-gingerbread-man',
    jackOLanter: 'fa-regular fa-jack-o-lantern',
    pinata: 'fa-regular fa-pinata',
    snowman: 'fa-regular fa-snowman',
    alien8Bit: 'fa-regular fa-alien-8bit',
    blockQuestion: 'fa-regular fa-block-question',
    chess: 'fa-regular fa-chess',
    dice: 'fa-regular fa-dice',
    gamepad: 'fa-regular fa-gamepad',
    joystick: 'fa-regular fa-joystick',
    pinball: 'fa-regular fa-pinball',
    puzzle: 'fa-regular fa-puzzle',
    baseballBat: 'fa-regular fa-baseball-bat',
    medal: 'fa-regular fa-medal',
    biohazard: 'fa-regular fa-biohazard',
    brain: 'fa-regular fa-brain',
    capsule: 'fa-regular fa-capsule',
    cauldron: 'fa-regular fa-cauldron',
    circleRadiation: 'fa-regular fa-circle-radiation',
    fire: 'fa-regular fa-fire',
    vial: 'fa-regular fa-vial',
    treasureChest: 'fa-regular fa-treasure-chest',
    sword: 'fa-regular fa-sword',
    dragon: 'fa-regular fa-dragon',
    ghost: 'fa-regular fa-ghost',
    dove: 'fa-regular fa-dove',
    rabbit: 'fa-regular fa-rabbit',
    horseSaddle: 'fa-regular fa-horse-saddle',
    dinosaur: 'fa-regular fa-dinosaur',
    ant: 'fa-regular fa-ant',
    solarSystem: 'fa-regular fa-solar-system',
    starShooting: 'fa-regular fa-star-shooting',
    telescope: 'fa-regular fa-telescope',
    puzzlePiece: 'fa-regular fa-puzzle-piece',
    bug: 'fa-regular fa-bug',
    cloudSun: 'fa-regular fa-cloud-sun',
    personRunning: 'fa-regular fa-person-running',
    bicycle: 'fa-regular fa-bicycle',
    courtSport: 'fa-regular fa-court-sport',
    bookOpenLines: 'fa-regular fa-book-open-lines',
    bookBlank: 'fa-regular fa-book-blank',
    personChalkboard: 'fa-regular fa-person-chalkboard',
    school: 'fa-regular fa-school',
    music: 'fa-regular fa-music',
    diploma: 'fa-regular fa-diploma',
    balloon: 'fa-regular fa-balloon',
    backpack: 'fa-regular fa-backpack',
    family: 'fa-regular fa-family',
    circus: 'fa-regular fa-tent-circus',
    candy: 'fa-regular fa-candy',
    cakeCandles: 'fa-regular fa-cake-candles',
    appleWhole: 'fa-regular fa-apple-whole',
    pizzaSlice: 'fa-regular fa-pizza-slice',
    popcorn: 'fa-regular fa-popcorn',
    mugHot: 'fa-regular fa-mug-hot',
    cupcake: 'fa-regular fa-cupcake',
    carBus: 'fa-regular fa-car-bus',
    carSide: 'fa-regular fa-car-side',
    truckMonster: 'fa-regular fa-truck-monster',
    truck: 'fa-regular fa-truck',
    motorcycle: 'fa-regular fa-motorcycle',
    tentCircus: 'fa-regular fa-tent-circus',
  };

  const contentTypes: ContentType[] = [
    { name: 'Book', route: 'books' },
    { name: 'Video', route: 'videos' },
    { name: 'Article', route: 'articles' },
    { name: 'Material', route: 'materials' },
  ];

  // Mainly for Video Collections.
  const realmToLanguageMap: Record<string, string> = {
    se: 'sv',
    no: 'nb',
    fi: 'fi',
  };

  const getTitleInRealmLanguage = (
    realm: string,
    multiLangVideoTitle: MultiLangVideoTitle[] | undefined | null,
  ): string => {
    // Ensure multiLangVideoTitle is an array
    if (!Array.isArray(multiLangVideoTitle)) return '';
    const targetLanguage = realmToLanguageMap[realm];
    // Try to find the title in the realm's language
    const matchingTitle = multiLangVideoTitle?.find((title) => title.languageCode === targetLanguage);
    // Fall back to the first available title or an empty string
    return matchingTitle?.value || multiLangVideoTitle[0]?.value || '';
  };

  const patchCollection = (original: ContentCollection, updated: ContentCollection): Partial<ContentCollection> => {
    return usePatchDiffer<ContentCollection>(original, updated);
  };

  return {
    CollectionsAvailableIcons,
    icons,
    contentTypes,
    patchCollection,
    getTitleInRealmLanguage,
  };
};
export default useCollectionUtils;
